export type PhaseAdvanceWhen = 'date' | 'previous_sold_out' | 'previous_expired' | 'manual';

export interface ActiveSalesPhaseInfo {
  name: string;
  endSaleDate?: string;
  advanceWhen?: PhaseAdvanceWhen;
  nextPhaseName?: string | null;
}

export interface LotCountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
  totalMs: number;
}

export function getTicketLotName(ticket: {
  salesPhaseName?: string;
  batchName?: string;
}): string | undefined {
  return ticket.salesPhaseName || ticket.batchName;
}

export function computeLotCountdown(endDate?: string | Date | null, now = new Date()): LotCountdownParts | null {
  if (!endDate) return null;
  const target = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(target.getTime())) return null;

  const totalMs = target.getTime() - now.getTime();
  if (totalMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true, totalMs: 0 };
  }

  const days = Math.floor(totalMs / 86400000);
  const hours = Math.floor((totalMs % 86400000) / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);

  return { days, hours, minutes, seconds, expired: false, totalMs };
}

export function formatCountdown(parts: LotCountdownParts): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  if (parts.days > 0) {
    return `${parts.days}d ${pad(parts.hours)}h ${pad(parts.minutes)}m`;
  }
  return `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
}

export function formatLotEndDate(endDate?: string | Date | null): string {
  if (!endDate) return '';
  const d = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getLotTransitionMessage(phase: ActiveSalesPhaseInfo): {
  showCountdown: boolean;
  subtitle: string;
} {
  const advanceWhen = phase.advanceWhen || 'date';
  const hasEnd = !!phase.endSaleDate;
  const next = phase.nextPhaseName;

  if (advanceWhen === 'previous_sold_out') {
    return {
      showCountdown: hasEnd,
      subtitle: next
        ? `Encerra quando esgotar · próximo: ${next}`
        : 'Encerra quando esgotar as vagas deste lote',
    };
  }

  if (advanceWhen === 'manual') {
    return {
      showCountdown: hasEnd,
      subtitle: next
        ? `Lote liberado manualmente · próximo: ${next}`
        : 'Lote liberado manualmente pela organização',
    };
  }

  if (hasEnd && next) {
    return {
      showCountdown: true,
      subtitle: `Depois abre ${next}`,
    };
  }

  if (hasEnd) {
    return {
      showCountdown: true,
      subtitle: 'Tempo restante neste lote',
    };
  }

  return {
    showCountdown: false,
    subtitle: next ? `Próximo lote: ${next}` : 'Lote em andamento',
  };
}
